import { Avatar, ButtonV2, PersonAddIcon, Tabs } from '@medeloop/medeloop-components-library';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetDataSourceById } from 'src/api/data-sources-queries';
import { NavigateBack, TitleSubTitle } from 'src/components';
import { ShareDatasets } from 'src/components/share-datasets/share-datasets';
import { useShareDatasets } from 'src/components/share-datasets/useShareDatasets';
import { selectIsUserAdmin } from 'src/store/auth';

const BASE_DATASETS_URL = '/account/data/datasets';

export function DataSetsDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const isUserAdmin = useSelector(selectIsUserAdmin);
  const { accountAppAudit } = useFlags();

  const { data, isLoading } = useGetDataSourceById(datasetId);

  const {
    invitedResearchers,
    invitedOrganizations,
    fetchInvitedResearchers,
    fetchInvitedAndInvitableOrganizations,
  } = useShareDatasets();

  const handleOnShareClick = () => {
    setIsShareModalOpen(true);
  };

  useEffect(() => {
    if (datasetId) {
      fetchInvitedResearchers(datasetId);
      fetchInvitedAndInvitableOrganizations(datasetId);
    }
  }, [datasetId]);

  return (
    <section className="flex flex-col">
      <div className="h-52  bg-dark-subdued-background -mb-10 border-b border-stroke-on-dark-subdued">
        <div className="max-w-right-section mx-auto px-6 xl:px-0">
          <NavigateBack to={BASE_DATASETS_URL} label="Data" />
          <div className="flex items-center justify-between h-20">
            <TitleSubTitle
              title={data?.displayName ?? ''}
              subTitle={data?.description ?? ''}
              isLoading={isLoading}
            />
            <div>
              <div className="flex gap-2">
                <div className="flex">
                  {invitedResearchers?.map((researcher) => (
                    <Avatar
                      key={researcher.id}
                      fullName={researcher.name}
                      size="sm"
                      className="-ml-2 border border-stroke-on-dark-subdued"
                    />
                  ))}
                  {invitedOrganizations?.map((organization) => (
                    <Avatar
                      key={organization.id}
                      fullName={organization.displayName}
                      size="sm"
                      className="-ml-2 border border-stroke-on-dark-subdued"
                    />
                  ))}
                </div>
                {isUserAdmin ? (
                  <ButtonV2 onClick={handleOnShareClick} startIcon={<PersonAddIcon />}>
                    Share
                  </ButtonV2>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-right-section w-full mx-auto px-6 xl:px-0">
        <Tabs
          value={location.pathname.split('/').pop()}
          onValueChange={(value) => navigate(`${BASE_DATASETS_URL}/${datasetId}/${value}`)}
        >
          <Tabs.List className="border-b-0">
            <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
            <Tabs.Trigger value="subject-characteristics">Subject characteristics</Tabs.Trigger>
            <Tabs.Trigger value="highlights">Highlights</Tabs.Trigger>
            <Tabs.Trigger value="cohorts">Cohorts</Tabs.Trigger>
            <Tabs.Trigger value="codes">Codes</Tabs.Trigger>
            {accountAppAudit && <Tabs.Trigger value="audit-log">Audit Log</Tabs.Trigger>}
          </Tabs.List>
          <div className="h-[calc(100vh-330px)] overflow-y-auto mt-8">
            <Tabs.Content value="overview">
              <Outlet />
            </Tabs.Content>
            <Tabs.Content value="subject-characteristics">
              <Outlet />
            </Tabs.Content>
            <Tabs.Content value="highlights">
              <Outlet />
            </Tabs.Content>
            <Tabs.Content value="cohorts">
              <Outlet />
            </Tabs.Content>
            <Tabs.Content value="codes">
              <Outlet />
            </Tabs.Content>
            {accountAppAudit && (
              <Tabs.Content value="audit-log">
                <Outlet />
              </Tabs.Content>
            )}
          </div>
        </Tabs>
      </div>
      <ShareDatasets
        isShareModalOpen={isShareModalOpen}
        setIsShareModalOpen={setIsShareModalOpen}
        dataset={data}
      />
    </section>
  );
}
